import React, { useState, useEffect, useMemo, useRef } from 'react';
import TimetableHeader from './TimetableHeader.jsx';
import TimetableSummary from './TimetableSummary.jsx';
import SubjectLoadPanel from './SubjectLoadPanel.jsx';
import TimetableGrid, { getDaySlotsFromSchedule, findSlotDataInDay } from './TimetableGrid.jsx';
import SubjectAssignmentDialog from './SubjectAssignmentDialog.jsx';
import BellScheduleManager from './BellScheduleManager.jsx';
import ReviewBeforePublishDialog from './ReviewBeforePublishDialog.jsx';
import timetableService from '../../services/TimetableService.js';
import subjectService from '../../services/SubjectService.js';
import teacherService from '../../services/TeacherService.js';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const BASE_CURRICULUM_SUBJECTS = [];

const TimetableManager = ({ subjects = [], teachers = [], classes = [], onTimetableUpdate }) => {
  const [academicYear, setAcademicYear] = useState('2026-2027');
  const [selectedClass, setSelectedClass] = useState('6th Standard');
  const [selectedSection, setSelectedSection] = useState('A');
  const lastNotifiedStateRef = useRef('');
  
  // Real DB Teachers State
  const [dbTeachers, setDbTeachers] = useState(teachers || []);
  const [dbSubjects, setDbSubjects] = useState(subjects || []);

  useEffect(() => {
    if (teachers && teachers.length > 0) {
      setDbTeachers(teachers);
    } else {
      const orgId = localStorage.getItem('orgId');
      if (orgId) {
        teacherService.getTeachersByOrg(orgId).then(res => {
          if (Array.isArray(res)) setDbTeachers(res);
          else if (res && res.data && Array.isArray(res.data)) setDbTeachers(res.data);
        }).catch(err => console.error('Failed to load teachers in timetable:', err));
      }
    }
  }, [teachers]);

  useEffect(() => {
    if (subjects && subjects.length > 0) {
      setDbSubjects(subjects);
    } else {
      const orgId = localStorage.getItem('orgId');
      if (orgId) {
        subjectService.getSubjectsByOrg(orgId).then(res => {
          if (Array.isArray(res)) setDbSubjects(res);
          else if (res && res.data && Array.isArray(res.data)) setDbSubjects(res.data);
        }).catch(err => console.error('Failed to load subjects in timetable:', err));
      }
    }
  }, [subjects]);
  
  // Real Subject Allocations fetched for selected Class + Section
  const [subjectAllocations, setSubjectAllocations] = useState([]);
  const [loadingAllocations, setLoadingAllocations] = useState(false);

  // Real Bell Schedule
  const [bellSchedule, setBellSchedule] = useState(null);
  const [showBellScheduleModal, setShowBellScheduleModal] = useState(false);

  // Active Timetable Record (Draft or Published)
  const [timetableRecord, setTimetableRecord] = useState(null);
  const [scheduleMap, setScheduleMap] = useState({});
  const [loadingTimetable, setLoadingTimetable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isEditingPublished, setIsEditingPublished] = useState(false);

  // Target Slot for Subject Assignment Dialog
  const [targetSlot, setTargetSlot] = useState(null);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);

  // Review Before Publish Dialog State
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  // Available Classes list derived from classes prop or fallback
  const availableClasses = useMemo(() => {
    if (classes && classes.length > 0) {
      const names = Array.from(new Set(classes.map(c => c.className || c.name).filter(Boolean)));
      if (names.length > 0) return names;
    }
    if (subjects && subjects.length > 0) {
      const names = Array.from(new Set(subjects.map(s => s.classLevel).filter(Boolean)));
      if (names.length > 0) return names;
    }
    return ['6th Standard', '7th Standard', '8th Standard', '9th Standard', '10th Standard'];
  }, [classes, subjects]);

  // Available Sections list
  const availableSections = useMemo(() => {
    if (classes && classes.length > 0) {
      const secs = Array.from(new Set(classes.filter(c => (c.className || c.name) === selectedClass).map(c => c.section).filter(Boolean)));
      if (secs.length > 0) return secs;
    }
    return ['A', 'B', 'C', 'D'];
  }, [classes, selectedClass]);

  // Ensure selectedClass is valid
  useEffect(() => {
    if (availableClasses.length > 0 && !availableClasses.includes(selectedClass)) {
      setSelectedClass(availableClasses[0]);
    }
  }, [availableClasses, selectedClass]);

  // 1. Fetch Bell Schedule
  useEffect(() => {
    const fetchBellSchedule = async () => {
      try {
        const schedules = await timetableService.getBellSchedules(academicYear);
        if (schedules && schedules.length > 0) {
          setBellSchedule(schedules[0]);
        }
      } catch (err) {
        console.error('Error fetching bell schedules:', err);
      }
    };
    fetchBellSchedule();
  }, [academicYear]);

  // 2. Fetch Subject Allocations for Selected Class & Section with Real DB Teachers
  useEffect(() => {
    const fetchAllocations = async () => {
      setLoadingAllocations(true);
      try {
        const orgId = localStorage.getItem('orgId') || 'SCH-02';
        const allAllocations = await subjectService.getOrgSubjectAllocations(orgId, academicYear);
        let classAllocations = [];

        if (Array.isArray(allAllocations) && allAllocations.length > 0) {
          classAllocations = allAllocations.filter(a => 
            (a.className && a.className.equalsIgnoreCase ? a.className.equalsIgnoreCase(selectedClass) : String(a.className).toLowerCase() === selectedClass.toLowerCase()) &&
            (a.sectionName && a.sectionName.equalsIgnoreCase ? a.sectionName.equalsIgnoreCase(selectedSection) : String(a.sectionName).toLowerCase() === selectedSection.toLowerCase())
          );
        }

        // Real subjects for this class from DB if available
        const realClassSubjects = (dbSubjects && dbSubjects.length > 0)
          ? dbSubjects.filter(s => s && s.classLevel && String(s.classLevel).toLowerCase() === selectedClass.toLowerCase())
          : [];
        const baseSubjects = realClassSubjects.length > 0
          ? realClassSubjects
          : (dbSubjects && dbSubjects.length > 0 ? dbSubjects : []);

        const resolveRealTeacher = (assignedTeacherName, assignedTeacherId) => {
          let teacherName = 'Not Assigned';
          let teacherId = '';

          // Priority 1: Match by teacher ID (primary identity)
          if (assignedTeacherId && dbTeachers && dbTeachers.length > 0) {
            const match = dbTeachers.find(t => String(t.teacherId || t.id) === String(assignedTeacherId));
            if (match) {
              return {
                teacherName: match.teacherName || match.name || match.fullName || 'Teacher',
                teacherId: match.teacherId || match.id || assignedTeacherId
              };
            }
          }

          // Priority 2: Match by teacher name
          if (assignedTeacherName && assignedTeacherName !== 'Unassigned' && dbTeachers && dbTeachers.length > 0) {
            const match = dbTeachers.find(t => 
              (t.teacherName && t.teacherName.toLowerCase() === assignedTeacherName.toLowerCase()) ||
              (t.name && t.name.toLowerCase() === assignedTeacherName.toLowerCase())
            );
            if (match) {
              return {
                teacherName: match.teacherName || match.name || match.fullName || assignedTeacherName,
                teacherId: match.teacherId || match.id || ''
              };
            }
          }

          // Priority 3: Fallback to name if not matched in list but explicitly assigned
          if (assignedTeacherName && assignedTeacherName !== 'Unassigned') {
            return {
              teacherName: assignedTeacherName,
              teacherId: assignedTeacherId || ''
            };
          }

          return { teacherName, teacherId };
        };

        if (classAllocations.length > 0) {
          // Resolve teacher names against real database teachers
          classAllocations = classAllocations.map((alloc) => {
            const associatedSubject = baseSubjects.find(s => String(s.subjectId || s.id) === String(alloc.subjectId));
            let tName = alloc.teacherName;
            let tId = alloc.teacherId;

            // Fallback to Subject record if allocation has no teacher assigned
            if (!tName || tName === 'Unassigned') {
              if (associatedSubject) {
                const subT = associatedSubject.teacherName || associatedSubject.assignedTeacher || associatedSubject.teacher;
                const subTId = associatedSubject.teacherId || associatedSubject.teacherCode;
                tName = subT;
                tId = subTId;
              }
            }

            const resolved = resolveRealTeacher(tName, tId);
            tName = resolved.teacherName;
            tId = resolved.teacherId;

            console.log(`[Timetable] Resolved teacher for allocated subject ${alloc.subjectName}: ID=${tId}, Name=${tName}`);

            return {
              ...alloc,
              teacherName: tName,
              teacherId: tId,
              className: selectedClass,
              sectionName: selectedSection
            };
          });
        } else {
          // Construct allocations using real DB subjects and real DB teachers
          classAllocations = baseSubjects.map((s, idx) => {
            const subName = s.subjectName || s.name || s.subjectCode || `Subject ${idx + 1}`;
            const subCode = s.subjectCode || s.code || subName.substring(0, 4).toUpperCase();
            const periods = s.theoryMarks ? Math.round((s.theoryMarks / 100) * 6) || 5 : (s.periodsPerWeek || 5);
            
            const subT = s.teacherName || s.assignedTeacher || s.teacher;
            const subTId = s.teacherId || s.teacherCode;
            const resolved = resolveRealTeacher(subT, subTId);

            console.log(`[Timetable] Resolved teacher for unallocated subject ${subName}: ID=${resolved.teacherId}, Name=${resolved.resolvedTeacherName || resolved.teacherName}`);

            return {
              id: s.id ? `ALLOC-${s.id}` : `SUB-${100 + idx}`,
              subjectId: s.subjectId || s.id || `SUB-${100 + idx}`,
              subjectName: subName,
              subjectCode: subCode,
              teacherName: resolved.teacherName,
              teacherId: resolved.teacherId,
              periodsPerWeek: periods,
              className: selectedClass,
              sectionName: selectedSection
            };
          });
        }

        setSubjectAllocations(classAllocations.filter(Boolean));
      } catch (err) {
        console.error('Error fetching subject allocations:', err);
        const fallbackAlloc = (dbSubjects || []).map((s, idx) => {
          return {
            id: s.id ? `ALLOC-${s.id}` : `SUB-${100 + idx}`,
            subjectId: s.subjectId || s.id || `SUB-${100 + idx}`,
            subjectName: s.subjectName || s.name || 'Subject',
            subjectCode: s.code || 'SUB',
            teacherName: 'Not Assigned',
            teacherId: '',
            periodsPerWeek: s.periodsPerWeek || 5,
            className: selectedClass,
            sectionName: selectedSection
          };
        });
        setSubjectAllocations(fallbackAlloc.filter(Boolean));
      } finally {
        setLoadingAllocations(false);
      }
    };

    fetchAllocations();
  }, [selectedClass, selectedSection, academicYear, dbSubjects, dbTeachers]);

  // Helper to normalize day keys to standard title-case ("Monday", "Tuesday", etc.)
  const normalizeScheduleMap = (rawMap) => {
    if (!rawMap || typeof rawMap !== 'object') return {};
    const normalized = {};
    const dayNameMap = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };

    Object.keys(rawMap).forEach(dayKey => {
      const lower = String(dayKey).toLowerCase().trim();
      const standardDay = dayNameMap[lower] || (dayKey.charAt(0).toUpperCase() + dayKey.slice(1));
      normalized[standardDay] = { ...(rawMap[dayKey] || {}) };
    });

    return normalized;
  };

  // 3. Fetch Active Timetable for Selected Class & Section
  useEffect(() => {
    const fetchTimetable = async () => {
      setLoadingTimetable(true);
      try {
        const record = await timetableService.getTimetableByClassAndSection(selectedClass, selectedSection, academicYear);
        if (record) {
          setTimetableRecord(record);
          setScheduleMap(normalizeScheduleMap(record.schedule || {}));
        } else {
          setTimetableRecord(null);
          setScheduleMap({});
        }
      } catch (err) {
        console.error('Error fetching timetable:', err);
      } finally {
        setLoadingTimetable(false);
      }
    };

    fetchTimetable();
  }, [selectedClass, selectedSection, academicYear]);

  // Notify parent of state update safely (prevent infinite re-render loops)
  useEffect(() => {
    if (onTimetableUpdate) {
      const record = timetableRecord ? {
        ...timetableRecord,
        schedule: scheduleMap
      } : (Object.keys(scheduleMap).length > 0 ? {
        academicYear,
        className: selectedClass,
        section: selectedSection,
        schedule: scheduleMap
      } : null);

      const stateSignature = JSON.stringify({
        recId: record?.id || record?.timetableId || null,
        status: record?.status || null,
        schedKeys: Object.keys(scheduleMap).map(d => `${d}:${Object.keys(scheduleMap[d] || {}).length}`).join(','),
        cls: selectedClass,
        sec: selectedSection,
        ay: academicYear
      });

      if (lastNotifiedStateRef.current !== stateSignature) {
        lastNotifiedStateRef.current = stateSignature;
        onTimetableUpdate(record, selectedClass, selectedSection);
      }
    }
  }, [timetableRecord, scheduleMap, selectedClass, selectedSection, academicYear, onTimetableUpdate]);

  // Compute Scheduled Counts per Subject
  const scheduledCounts = useMemo(() => {
    const counts = {};
    Object.keys(scheduleMap).forEach(day => {
      const daySlots = scheduleMap[day] || {};
      Object.keys(daySlots).forEach(timeKey => {
        const slot = daySlots[timeKey];
        if (slot && slot.subject && slot.slotType === 'TEACHING') {
          counts[slot.subject] = (counts[slot.subject] || 0) + 1;
        }
      });
    });
    return counts;
  }, [scheduleMap]);

  // Calculate Metrics for Summary KPI Bar (Teaching Slots ONLY, exclude breaks)
  const bellSlots = bellSchedule?.slots || [
    { slotOrder: 1, label: 'Period 1', startTime: '09:00', endTime: '09:45', slotType: 'TEACHING' },
    { slotOrder: 2, label: 'Period 2', startTime: '09:45', endTime: '10:30', slotType: 'TEACHING' },
    { slotOrder: 3, label: 'Short Break', startTime: '10:30', endTime: '10:45', slotType: 'SHORT_BREAK' },
    { slotOrder: 4, label: 'Period 3', startTime: '10:45', endTime: '11:30', slotType: 'TEACHING' },
    { slotOrder: 5, label: 'Period 4', startTime: '11:30', endTime: '12:15', slotType: 'TEACHING' },
    { slotOrder: 6, label: 'Lunch Break', startTime: '12:15', endTime: '13:00', slotType: 'LUNCH' },
    { slotOrder: 7, label: 'Period 5', startTime: '13:00', endTime: '13:45', slotType: 'TEACHING' },
    { slotOrder: 8, label: 'Period 6', startTime: '13:45', endTime: '14:30', slotType: 'TEACHING' },
    { slotOrder: 9, label: 'Period 7', startTime: '14:30', endTime: '15:15', slotType: 'TEACHING' },
    { slotOrder: 10, label: 'Period 8', startTime: '15:15', endTime: '16:00', slotType: 'TEACHING' },
  ];

  const teachingSlotsCount = bellSlots.filter(s => s.slotType === 'TEACHING').length;
  const totalSlots = teachingSlotsCount * 6; // 6 working days (e.g. 8 teaching slots * 6 = 48 or 9 * 6 = 54)
  const assignedSlots = Object.values(scheduledCounts).reduce((a, b) => a + b, 0);
  const availableSlots = Math.max(0, totalSlots - assignedSlots);
  const completionRate = totalSlots > 0 ? Math.min(100, Math.round((assignedSlots / totalSlots) * 100)) : 0;

  // Cell Click Handler
  const handleCellClick = (slotInfo) => {
    setTargetSlot(slotInfo);
    setShowAssignmentDialog(true);
  };

  // Assign Subject Slot
  const handleAssignSlot = (dayOfWeek, timeKey, slotPayload) => {
    setScheduleMap(prev => {
      const targetDay = String(dayOfWeek).trim();
      const existingKey = Object.keys(prev).find(k => k.toLowerCase() === targetDay.toLowerCase()) || targetDay;
      const updatedDay = { ...(prev[existingKey] || {}) };
      updatedDay[timeKey] = slotPayload;
      return { ...prev, [existingKey]: updatedDay };
    });
  };

  // Clear Single Slot
  const handleClearSlot = (dayOfWeek, timeKey) => {
    setScheduleMap(prev => {
      const targetDay = String(dayOfWeek).trim();
      const existingKey = Object.keys(prev).find(k => k.toLowerCase() === targetDay.toLowerCase()) || targetDay;
      const updatedDay = { ...(prev[existingKey] || {}) };
      delete updatedDay[timeKey];
      return { ...prev, [existingKey]: updatedDay };
    });
  };

  // Clear Entire Draft
  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear all slots in this timetable draft?')) {
      setScheduleMap({});
    }
  };

  // Save Draft
  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      const res = await timetableService.saveTimetableDraft({
        className: selectedClass,
        section: selectedSection,
        academicYear,
        schedule: scheduleMap
      });
      if (res && res.timetable) {
        setTimetableRecord(res.timetable);
        if (res.timetable.schedule) {
          setScheduleMap(normalizeScheduleMap(res.timetable.schedule));
        }
      }
      alert('✓ Timetable draft saved successfully!');
    } catch (err) {
      console.error('Failed to save timetable draft:', err);
      alert('Failed to save draft: ' + (err.message || 'Validation error occurred'));
    } finally {
      setSaving(false);
    }
  };

  // Trigger Review Before Publish Dialog
  const handleOpenPublishReview = () => {
    setShowReviewDialog(true);
  };

  // Confirmed Publish Action
  const handleConfirmPublish = async () => {
    setShowReviewDialog(false);
    try {
      setSaving(true);
      const draftRes = await timetableService.saveTimetableDraft({
        className: selectedClass,
        section: selectedSection,
        academicYear,
        schedule: scheduleMap
      });
      if (draftRes && draftRes.timetable) {
        setTimetableRecord(draftRes.timetable);
        if (draftRes.timetable.schedule) {
          setScheduleMap(normalizeScheduleMap(draftRes.timetable.schedule));
        }
        await doPublish(draftRes.timetable.timetableId);
      } else {
        alert('Failed to save timetable draft before publishing.');
      }
    } catch (err) {
      console.error('Save draft before publish failed:', err);
      alert('Cannot publish: ' + (err.message || 'Save draft failed'));
    } finally {
      setSaving(false);
    }
  };

  const doPublish = async (id) => {
    try {
      setPublishing(true);
      const res = await timetableService.publishTimetable(id);
      if (res && res.timetable) {
        setTimetableRecord(res.timetable);
        if (res.timetable.schedule) {
          setScheduleMap(normalizeScheduleMap(res.timetable.schedule));
        }
      }
      alert(`✓ Timetable published successfully for ${selectedClass} Section ${selectedSection}!`);
    } catch (err) {
      console.error('Failed to publish timetable:', err);
      alert('Cannot publish timetable: ' + (err.message || 'Validation error'));
    } finally {
      setPublishing(false);
    }
  };

  // Strictly Quota-Enforced Auto-Generate
  const handleAutoGenerateTimetable = () => {
    const teachingSlots = bellSlots.filter(s => s.slotType === 'TEACHING');
    const newScheduleMap = {};

    const resolveRealTeacher = (assignedTeacherName, assignedTeacherId) => {
      let teacherName = 'Not Assigned';
      let teacherId = '';

      if (assignedTeacherId && dbTeachers && dbTeachers.length > 0) {
        const match = dbTeachers.find(t => String(t.teacherId || t.id) === String(assignedTeacherId));
        if (match) {
          return {
            teacherName: match.teacherName || match.name || match.fullName || 'Teacher',
            teacherId: match.teacherId || match.id || assignedTeacherId
          };
        }
      }

      if (assignedTeacherName && assignedTeacherName !== 'Unassigned' && dbTeachers && dbTeachers.length > 0) {
        const match = dbTeachers.find(t => 
          (t.teacherName && t.teacherName.toLowerCase() === assignedTeacherName.toLowerCase()) ||
          (t.name && t.name.toLowerCase() === assignedTeacherName.toLowerCase())
        );
        if (match) {
          return {
            teacherName: match.teacherName || match.name || match.fullName || assignedTeacherName,
            teacherId: match.teacherId || match.id || ''
          };
        }
      }

      if (assignedTeacherName && assignedTeacherName !== 'Unassigned') {
        return {
          teacherName: assignedTeacherName,
          teacherId: assignedTeacherId || ''
        };
      }

      return { teacherName, teacherId };
    };

    const availableSubs = subjectAllocations.length > 0 ? subjectAllocations : (
      (dbSubjects && dbSubjects.length > 0 ? dbSubjects : BASE_CURRICULUM_SUBJECTS).map((s, idx) => {
        const subName = s.subjectName || s.name || s.subjectCode || `Subject ${idx + 1}`;
        const subCode = s.subjectCode || s.code || subName.substring(0, 4).toUpperCase();
        
        const subT = s.teacherName || s.assignedTeacher || s.teacher;
        const subTId = s.teacherId || s.teacherCode;
        const resolved = resolveRealTeacher(subT, subTId);

        return {
          id: s.id ? `ALLOC-${s.id}` : `SUB-${100 + idx}`,
          subjectId: s.subjectId || s.id || `SUB-${100 + idx}`,
          subjectName: subName,
          subjectCode: subCode,
          teacherName: resolved.teacherName,
          teacherId: resolved.teacherId,
          periodsPerWeek: s.periodsPerWeek || 5,
          className: selectedClass,
          sectionName: selectedSection
        };
      })
    );

    // Track periods assigned per subject to NEVER exceed subject.periodsPerWeek
    const periodTrack = {};
    availableSubs.forEach(s => {
      periodTrack[s.subjectName || s.subjectCode] = 0;
    });

    let subIndex = 0;

    DAYS.forEach((day) => {
      newScheduleMap[day] = {};
      teachingSlots.forEach((slot, slotIdx) => {
        const startClean = (slot.startTime ? String(slot.startTime).trim() : '09:00');
        const endClean = (slot.endTime ? String(slot.endTime).trim() : '09:45');
        const timeKey = `${startClean}-${endClean}`;

        // Find next subject whose weekly quota is not yet met
        let chosenSub = null;
        for (let attempt = 0; attempt < availableSubs.length; attempt++) {
          const candidate = availableSubs[(subIndex + attempt) % availableSubs.length];
          const subName = candidate.subjectName || candidate.subjectCode;
          const req = candidate.periodsPerWeek || 5;

          if ((periodTrack[subName] || 0) < req) {
            chosenSub = candidate;
            subIndex = (subIndex + attempt + 1) % availableSubs.length;
            break;
          }
        }

        if (chosenSub) {
          const chosenName = chosenSub.subjectName || chosenSub.subjectCode;
          periodTrack[chosenName] = (periodTrack[chosenName] || 0) + 1;

          newScheduleMap[day][timeKey] = {
            subjectId: chosenSub.subjectId || null,
            subject: chosenName,
            subjectCode: chosenSub.subjectCode || '',
            subjectAllocationId: chosenSub.id || null,
            bellScheduleSlotId: slot.id || null,
            slotOrder: slot.slotOrder || slotIdx + 1,
            teacherId: chosenSub.teacherId || '',
            teacher: chosenSub.teacherName || 'Unassigned',
            startTime: startClean,
            endTime: endClean,
            slotType: 'TEACHING'
          };
        }
      });
    });

    setScheduleMap(newScheduleMap);
    alert(`⚡ Timetable auto-generated for ${selectedClass} Section ${selectedSection}! Click "Save & Continue Later" or "Publish Timetable" to keep your changes.`);
  };
  // Check if active timetable is published
  const isPublished = timetableRecord?.status === 'PUBLISHED';
  const isReadOnly = isPublished && !isEditingPublished;

  // Dedicated clean print handler — opens a fresh window with only the timetable table
  const handlePrintTimetable = () => {
    const orgName = localStorage.getItem('orgName') || 'SHRI SHARADA ENGLISH MEDIUM SCHOOL';
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Build table rows from scheduleMap
    const slotRows = bellSlots.map((slot, sIdx) => {
      const isBreak = slot.slotType === 'SHORT_BREAK' || slot.slotType === 'LUNCH';
      const rowBg = isBreak ? '#f1f5f9' : '#fff';
      const cells = days.map(day => {
        if (isBreak) {
          return `<td style="background:#e2e8f0;color:#475569;font-style:italic;font-size:11px;text-align:center;border:1px solid #ccc;padding:6px;">${slot.label}</td>`;
        }
        const dayObj = getDaySlotsFromSchedule ? getDaySlotsFromSchedule(scheduleMap, day) : (scheduleMap[day] || {});
        const slotData = findSlotDataInDay ? findSlotDataInDay(dayObj, slot, sIdx) : (dayObj[`${slot.startTime}-${slot.endTime}`] || null);
        if (slotData && slotData.subject) {
          return `<td style="text-align:center;border:1px solid #ccc;padding:6px;background:#eff6ff;">
            <div style="font-weight:700;font-size:11px;color:#1e40af;">${slotData.subject}</div>
            <div style="font-size:10px;color:#475569;margin-top:2px;">${slotData.teacher || ''}</div>
          </td>`;
        }
        return `<td style="text-align:center;border:1px solid #ccc;padding:6px;color:#94a3b8;font-size:10px;">—</td>`;
      }).join('');

      return `<tr style="background:${rowBg};">
        <td style="border:1px solid #ccc;padding:6px;font-weight:700;font-size:11px;background:#f8fafc;white-space:nowrap;">
          <div>${slot.label}</div>
          <div style="font-weight:400;font-size:10px;color:#64748b;font-family:monospace;">${slot.startTime}–${slot.endTime}</div>
        </td>
        ${cells}
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Timetable — ${selectedClass} Sec ${selectedSection}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; padding: 20px; background: white; color: #000; }
    h1 { text-align: center; font-size: 18px; margin-bottom: 4px; }
    h2 { text-align: center; font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    thead th { background: #1e40af; color: #fff; padding: 8px; text-align: center; border: 1px solid #1e3a8a; font-size: 11px; letter-spacing: 0.04em; }
    thead th:first-child { background: #0f172a; }
    @media print { @page { margin: 10mm; size: landscape; } }
  </style>
</head>
<body>
  <h1>${orgName}</h1>
  <h2>Class Weekly Timetable: ${selectedClass} — Section ${selectedSection} (${academicYear})</h2>
  <table>
    <thead>
      <tr>
        <th style="width:110px;">TIME / PERIOD</th>
        ${days.map(d => `<th>${d.toUpperCase()}</th>`).join('')}
      </tr>
    </thead>
    <tbody>${slotRows}</tbody>
  </table>
  <div style="margin-top:16px;font-size:10px;color:#64748b;text-align:right;">Printed on: ${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</div>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=1100,height=750');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 400);
  };

  return (
    <div className="timetable-manager-root">

      {/* WORKFLOW GUIDE */}
      <div className="no-print workflow-guide-container" style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #BFDBFE',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px'
      }}>
        <div style={{ fontSize: '15px', fontWeight: '800', color: '#1E3A8A', marginBottom: '4px' }}>
          Create Class Timetable
        </div>
        <div style={{ fontSize: '12.5px', color: '#3B82F6', marginBottom: '14px' }}>
          Choose a class and section. Subjects and assigned teachers are loaded automatically. Schedule the required periods during the week, review any issues, and publish the final timetable.
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { step: 1, label: 'Select Class' },
            { step: 2, label: 'Check Subject Allocation' },
            { step: 3, label: 'Build Timetable' },
            { step: 4, label: 'Review' },
            { step: 5, label: 'Publish' }
          ].map((item, idx) => (
            <div key={item.step} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12.5px',
                fontWeight: '700',
                color: '#1D4ED8'
              }}>
                <span style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#2563EB',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px'
                }}>
                  {item.step}
                </span>
                {item.label}
              </div>
              {idx < 4 && <span style={{ color: '#93C5FD', fontSize: '14px' }}>➔</span>}
            </div>
          ))}
        </div>
      </div>

      {/* HEADER TOOLBAR */}
      <div className="no-print header-toolbar">
        <TimetableHeader
          academicYear={academicYear}
          setAcademicYear={setAcademicYear}
          selectedClass={selectedClass}
          setSelectedClass={setSelectedClass}
          availableClasses={availableClasses}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          availableSections={availableSections}
          status={timetableRecord?.status || 'DRAFT'}
          onOpenBellSchedule={() => setShowBellScheduleModal(true)}
          onAutoGenerate={handleAutoGenerateTimetable}
          onClearDraft={handleClearDraft}
          onSaveDraft={handleSaveDraft}
          onPublish={handleOpenPublishReview}
          onToggleEdit={() => setIsEditingPublished(!isEditingPublished)}
          onPrint={handlePrintTimetable}
          saving={saving}
          publishing={publishing}
          readOnly={isReadOnly}
        />
      </div>

      {/* SUMMARY KPI BAR */}
      <div className="no-print summary-kpi-bar-container">
        <TimetableSummary
          totalSlots={totalSlots}
          assignedSlots={assignedSlots}
          availableSlots={availableSlots}
          completionRate={completionRate}
          conflictCount={0}
        />
      </div>

      {/* WEEKLY SUBJECT REQUIREMENTS PANEL */}
      <div className="no-print subject-load-panel-container">
        <SubjectLoadPanel
          subjectAllocations={subjectAllocations}
          scheduledCounts={scheduledCounts}
        />
      </div>

      {/* WEEKLY TIMETABLE GRID */}
      {loadingTimetable ? (
        <div style={{ padding: '48px', textAlign: 'center', background: 'white', borderRadius: '14px', border: '1px solid #E2E8F0', color: '#64748B' }}>
          Loading class timetable...
        </div>
      ) : (
        <div className="timetable-print-container">
          <TimetableGrid
            bellScheduleSlots={bellSlots}
            scheduleMap={scheduleMap}
            onCellClick={handleCellClick}
            readOnly={isReadOnly}
            academicYear={academicYear}
            timetableId={timetableRecord?.timetableId}
          />
        </div>
      )}

      {/* ADD SUBJECT DIALOG */}
      <SubjectAssignmentDialog
        show={showAssignmentDialog}
        onClose={() => setShowAssignmentDialog(false)}
        targetSlot={targetSlot}
        subjectAllocations={subjectAllocations}
        teachers={dbTeachers}
        scheduledCounts={scheduledCounts}
        onAssignSlot={handleAssignSlot}
        onClearSlot={handleClearSlot}
      />

      {/* REVIEW BEFORE PUBLISH DIALOG */}
      <ReviewBeforePublishDialog
        show={showReviewDialog}
        onClose={() => setShowReviewDialog(false)}
        onConfirmPublish={handleConfirmPublish}
        selectedClass={selectedClass}
        selectedSection={selectedSection}
        academicYear={academicYear}
        subjectAllocations={subjectAllocations}
        scheduledCounts={scheduledCounts}
        conflictCount={0}
        publishing={publishing}
      />

      {/* BELL SCHEDULE MANAGER MODAL */}
      <BellScheduleManager
        show={showBellScheduleModal}
        onClose={() => setShowBellScheduleModal(false)}
        currentSchedule={bellSchedule}
        onScheduleSaved={(newSched) => setBellSchedule(newSched)}
      />
    </div>
  );
};

export default TimetableManager;
